<script setup lang="ts">
withDefaults(defineProps<{
  lazyLoading?: boolean;
}>(), {
  lazyLoading: true,
});

const { t } = useI18n();
const { openVideoModal } = useVideoModal();

const featureKeys = ['voice', 'variables', 'translations', 'suggestions', 'duplicate', 'signatures'] as const;
const featureIcons: Record<typeof featureKeys[number], string> = {
  voice: "lucide:mic",
  variables: "lucide:braces",
  translations: "lucide:languages",
  suggestions: "heroicons:sparkles",
  duplicate: "lucide:files",
  signatures: "lucide:signature",
};
const features = computed(() => featureKeys.map(key => ({
  name: t(`features.items.${key}.name`),
  description: t(`features.items.${key}.description`),
  icon: featureIcons[key],
})));
</script>

<template>
  <div
    id="fonctionnalites"
    class="my-24 scroll-mt-10 sm:my-32 xl:my-48"
  >
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <div class="mx-auto max-w-2xl sm:text-center">
        <h2 class="text-base/7 font-semibold text-primary-600">
          {{ $t('features.sectionLabel') }}
        </h2>
        <p class="mt-2 text-pretty font-serif text-4xl font-semibold text-gray-900 sm:text-balance sm:text-5xl">
          {{ $t('features.title') }}
        </p>
        <p class="mx-auto mt-6 max-w-lg text-lg/8 text-gray-600">
          {{ $t('features.subtitle') }}
        </p>
      </div>
    </div>
    <div class="relative overflow-hidden pt-16">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div
          role="button"
          tabindex="0"
          :aria-label="$t('features.videoAriaLabel')"
          class="group relative cursor-pointer"
          @click="openVideoModal"
          @keydown.enter="openVideoModal"
          @keydown.space.prevent="openVideoModal"
        >
          <NuxtImg
            src="/assets/screenshot-1.png"
            :loading="lazyLoading ? 'lazy' : 'eager'"
            :alt="$t('features.screenshotAlt')"
            class="rounded-t-lg shadow-2xl ring-1 ring-gray-900/10 sm:rounded-t-xl"
            sizes="sm:100vw md:100vw lg:1216px"
          />
          <AppPlayButtonOverlay />
        </div>
        <div
          class="relative"
          aria-hidden="true"
        >
          <div class="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-white pt-[7%]" />
        </div>
      </div>
    </div>
    <div class="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
      <dl class="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base/7 text-gray-600 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
        <div
          v-for="feature in features"
          :key="feature.name"
          class="relative pl-9"
        >
          <dt class="inline font-semibold text-gray-900">
            <Icon
              :name="feature.icon"
              class="absolute left-1 top-1 size-5 text-primary-600"
            />
            {{ feature.name }}
          </dt>
          {{ ' ' }}
          <dd class="inline">
            {{ feature.description }}
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>
